import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useAiIntelligenceStore } from '../store/ai-intelligence-store';
import { formatCurrency } from '@/lib/utils/format';
import { cn } from '@/lib/utils/cn';
import { Sparkles, AlertTriangle, TrendingUp, TrendingDown, Minus, RefreshCw } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export function InsightsTab() {
  const [period, setPeriod] = useState('month');
  const [focus, setFocus] = useState('all');

  const { insights, isLoadingInsights, insightsError, fetchInsights } = useAiIntelligenceStore();

  // Cargar al montar con defaults
  useEffect(() => {
    fetchInsights(period, focus);
  }, []);

  const handleRefresh = () => {
    fetchInsights(period, focus);
  };

  const periodOptions = [
    { value: 'week', label: 'Semana' },
    { value: 'month', label: 'Mes' },
    { value: 'quarter', label: 'Trimestre' },
  ];

  const focusOptions = [
    { value: 'all', label: 'Todos' },
    { value: 'margins', label: 'Márgenes' },
    { value: 'approvals', label: 'Aprobaciones' },
    { value: 'revenue', label: 'Ingresos' },
  ];

  return (
    <div className="space-y-6">
      {/* Controles */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Configurar Análisis</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Elige un periodo de tiempo y un area de enfoque. El sistema analizara todas las
            cotizaciones de ese periodo, comparara con el periodo anterior, y generara narrativas
            automaticas con los hallazgos más importantes.
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Periodo</label>
              <div className="flex gap-1">
                {periodOptions.map((p) => (
                  <button
                    key={p.value}
                    onClick={() => setPeriod(p.value)}
                    className={cn(
                      'px-3 py-1.5 text-xs rounded-md font-medium transition-colors',
                      period === p.value
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                    )}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Foco</label>
              <div className="flex gap-1">
                {focusOptions.map((f) => (
                  <button
                    key={f.value}
                    onClick={() => setFocus(f.value)}
                    className={cn(
                      'px-3 py-1.5 text-xs rounded-md font-medium transition-colors',
                      focus === f.value
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                    )}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            <Button onClick={handleRefresh} disabled={isLoadingInsights} className="mt-auto">
              <RefreshCw className={cn('w-4 h-4 mr-2', isLoadingInsights && 'animate-spin')} />
              Generar
            </Button>
          </div>
        </CardContent>
      </Card>

      {isLoadingInsights && (
        <div className="space-y-4">
          <Skeleton className="h-32" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
          <Skeleton className="h-64" />
        </div>
      )}

      {insightsError && (
        <Card className="border-destructive/50">
          <CardContent className="pt-6 flex items-center gap-3 text-destructive">
            <AlertTriangle className="w-5 h-5" />
            <p>{insightsError}</p>
          </CardContent>
        </Card>
      )}

      {insights && !isLoadingInsights && (
        <div className="space-y-6">
          {/* Narrativas AI */}
          <Card className="border-primary/20 bg-primary/5">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary" />
                Narrativa de Negocio — {insights.periodo}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {insights.narrativa.map((n, i) => (
                  <li key={i} className="flex gap-2 text-sm">
                    <span className="text-primary font-bold">•</span>
                    <span>{n}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KPICard
              label="Revenue"
              current={formatCurrency(insights.metricas.revenue)}
              previous={formatCurrency(insights.metricas.revenueAnterior)}
              change={insights.metricas.cambioRevenue}
            />
            <KPICard
              label="Margen Promedio"
              current={insights.metricas.margenPromedio}
              previous={insights.metricas.margenAnterior}
              change={insights.metricas.cambioMargen}
            />
            <KPICard
              label="Tasa Aprobación"
              current={insights.metricas.tasaAprobacion}
              previous={insights.metricas.tasaAprobacionAnterior}
              change=""
            />
            <KPICard
              label="Volumen"
              current={String(insights.metricas.volumen)}
              previous={String(insights.metricas.volumenAnterior)}
              change={`${insights.metricas.volumen - insights.metricas.volumenAnterior >= 0 ? '+' : ''}${insights.metricas.volumen - insights.metricas.volumenAnterior}`}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Clientes */}
            {insights.topClientes && insights.topClientes.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Top Clientes por Revenue</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={insights.topClientes} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted/30" />
                      <XAxis
                        type="number"
                        tick={{ fontSize: 11 }}
                        tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`}
                      />
                      <YAxis
                        type="category"
                        dataKey="cliente"
                        tick={{ fontSize: 11 }}
                        width={120}
                      />
                      <Tooltip
                        formatter={(value) => [formatCurrency(Number(value)), 'Revenue']}
                        contentStyle={{
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                        }}
                      />
                      <Bar dataKey="revenue" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Top Vendedores */}
            {insights.topVendedores && insights.topVendedores.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Top Vendedores por Revenue</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {insights.topVendedores.map((v, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between p-2.5 rounded-lg bg-muted/20"
                      >
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">
                            {i + 1}
                          </span>
                          <span className="text-sm font-medium">{v.vendedor}</span>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold">{formatCurrency(v.revenue)}</p>
                          <p className="text-xs text-muted-foreground">
                            {v.cotizaciones} cotizaciones
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Anomalías */}
          {insights.anomalias && insights.anomalias.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  Anomalías Detectadas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {insights.anomalias.map((a, i) => (
                    <div
                      key={i}
                      className={cn(
                        'flex items-center justify-between p-3 rounded-lg',
                        a.tipo === 'margen_alto'
                          ? 'bg-emerald-100 dark:bg-emerald-900/20'
                          : 'bg-red-100 dark:bg-red-900/20'
                      )}
                    >
                      <span className="text-sm font-medium">{a.cliente}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{a.margen}</span>
                        <span
                          className={cn(
                            'text-xs px-2 py-0.5 rounded-full font-medium',
                            a.tipo === 'margen_alto'
                              ? 'bg-emerald-200 text-emerald-800 dark:bg-emerald-800 dark:text-emerald-200'
                              : 'bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200'
                          )}
                        >
                          {a.tipo === 'margen_alto' ? 'Alto' : 'Bajo'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {!insights && !isLoadingInsights && !insightsError && (
        <Card>
          <CardContent className="pt-6 text-center text-muted-foreground py-12">
            <Sparkles className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p className="text-lg font-medium mb-1">Insights de Negocio</p>
            <p className="text-sm max-w-lg mx-auto">
              Presiona &quot;Generar&quot; arriba para obtener un dashboard inteligente con:
              narrativas automaticas de tu negocio, KPIs comparados vs periodo anterior, ranking de
              clientes y vendedores, y detección de anomalias en márgenes.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function KPICard({
  label,
  current,
  previous,
  change,
}: {
  label: string;
  current: string;
  previous: string;
  change: string;
}) {
  const isPositive = change.startsWith('+');
  const isNeutral = change === '0' || change === '' || change === '0%';
  const TrendIcon = isNeutral ? Minus : isPositive ? TrendingUp : TrendingDown;

  return (
    <Card>
      <CardContent className="pt-4 pb-3">
        <p className="text-xs text-muted-foreground mb-1">{label}</p>
        <p className="text-xl font-bold">{current}</p>
        <div className="flex items-center gap-1 mt-1">
          <TrendIcon
            className={cn(
              'w-3 h-3',
              isNeutral ? 'text-muted-foreground' : isPositive ? 'text-emerald-600' : 'text-red-600'
            )}
          />
          <span
            className={cn(
              'text-xs',
              isNeutral ? 'text-muted-foreground' : isPositive ? 'text-emerald-600' : 'text-red-600'
            )}
          >
            {change || 'sin cambio'}
          </span>
          <span className="text-xs text-muted-foreground ml-1">vs {previous}</span>
        </div>
      </CardContent>
    </Card>
  );
}
